import React, { useState, useEffect, useRef, useCallback } from 'react';
import { OWN_CONTROL_FACETS, filterOptionLabel } from '../logic/dashboard';

// Multi-trigger filter bar over the dashboard's generic `filters` envelope
// (filter-envelope shape owned by apiv2's dashboard view PU).
// File name ≠ export name: kept as ConditionFilter.jsx for blast-radius reasons.
export default function DashboardFilters({ filters = [], selectedFilters = {}, onChange }) {
  // Which filter's popover is currently open. Only one at a time.
  const [openKey, setOpenKey] = useState(null);
  // {key → wrapper div} so we can detect click-outside per active chip.
  const wrapRefs = useRef({});

  useEffect(() => {
    if (!openKey) return undefined;
    const onClick = (e) => {
      const el = wrapRefs.current[openKey];
      if (el && !el.contains(e.target)) setOpenKey(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenKey(null); };
    // focusin too: opening the sub-track menu from the keyboard closes this one.
    document.addEventListener('mousedown', onClick);
    document.addEventListener('focusin', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('focusin', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [openKey]);

  // Keys actually filtering the roster. Read off `selectedFilters` rather than
  // `filters` so a selection whose facet the server didn't return for the current
  // cohort (e.g. a deep-linked value with no matching row) is still clearable.
  const activeKeys = Object.keys(selectedFilters)
    .filter((k) => Array.isArray(selectedFilters[k]) && selectedFilters[k].length > 0);

  // Facets with their own control (the CMS goals cards, the sub-track menu, the Ready to submit chip):
  // drawn here they would be a second, broken chip.
  const filterList = (Array.isArray(filters) ? filters : [])
    .filter((f) => !OWN_CONTROL_FACETS.includes(f?.key))
    .map((f) => (Array.isArray(f.options)
      ? { ...f, options: f.options.map((o) => ({ ...o, label: filterOptionLabel(f.key, o.value, o.label) })) }
      : f));
  // Nothing to show AND nothing to clear — otherwise keep the bar so an active
  // filter never becomes unclearable.
  if (filterList.length === 0 && activeKeys.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {filterList.map((f) => (
        <FilterChip
          key={f.key}
          filter={f}
          selected={selectedFilters[f.key] || []}
          open={openKey === f.key}
          onToggleOpen={() => setOpenKey((prev) => (prev === f.key ? null : f.key))}
          onChange={(next) => onChange?.(f.key, next)}
          setWrapRef={(el) => { wrapRefs.current[f.key] = el; }}
        />
      ))}
      {activeKeys.length > 0 && (
        <button
          type="button"
          className="filter-clear"
          onClick={() => activeKeys.forEach((k) => onChange?.(k, []))}
        >
          {activeKeys.length === 1 ? 'Clear filter' : `Clear all ${activeKeys.length} filters`}
        </button>
      )}
    </div>
  );
}

function FilterChip({ filter, selected, open, onToggleOpen, onChange, setWrapRef }) {
  const isSingleSelect = filter.type === 'single-select';
  const count = selected.length;
  const hasSelection = count > 0;

  // Display options in priority_order (if declared) — server is source of truth
  // for the canonical render order. Defensive fallback appends any options not
  // in priority_order so nothing disappears if the lists drift.
  const orderedOptions = useCallback(() => {
    const opts = filter.options || [];
    if (!Array.isArray(filter.priority_order) || filter.priority_order.length === 0) return opts;
    const byValue = Object.fromEntries(opts.map((o) => [o.value, o]));
    const seen = new Set();
    const ordered = [];
    for (const v of filter.priority_order) {
      if (byValue[v]) { ordered.push(byValue[v]); seen.add(v); }
    }
    for (const o of opts) { if (!seen.has(o.value)) ordered.push(o); }
    return ordered;
  }, [filter.options, filter.priority_order])();

  const toggleOption = (value) => {
    if (isSingleSelect) {
      // Radio: clicking the active option clears; otherwise replace selection.
      onChange(selected.includes(value) ? [] : [value]);
    } else {
      onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    }
  };
  const clearAll = () => onChange([]);

  // Journey-step groups (open_task facet): listed above the task types, and a zero-count
  // group stays hidden unless already selected — it would only produce an empty roster.
  const groups = Array.isArray(filter.groups) ? filter.groups : [];
  const visibleGroups = groups.filter((g) => (typeof g.count === 'number' && g.count > 0) || selected.includes(g.value));
  const isAnd = filter.semantics === 'and';

  // "Label: Value" for one group / a single-select pick; "· all of N" when the server ANDs
  // (two big counts can empty the roster); else "· N".
  let chipLabel = filter.label;
  if (hasSelection) {
    const soleGroup = count === 1 ? groups.find((g) => g.value === selected[0]) : undefined;
    if (isSingleSelect) {
      const opt = orderedOptions.find((o) => o.value === selected[0]);
      chipLabel = opt ? `${filter.label}: ${opt.label}` : `${filter.label} · ${count}`;
    } else if (soleGroup) {
      chipLabel = `${filter.label}: ${soleGroup.label}`;
    } else if (isAnd && count > 1) {
      chipLabel = `${filter.label} · all of ${count}`;
    } else {
      chipLabel = `${filter.label} · ${count}`;
    }
  }
  const inputType = isSingleSelect ? 'radio' : 'checkbox';

  return (
    <div ref={setWrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onToggleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`filter-chip${hasSelection ? ' is-selected' : ''}`}
      >
        <span>{chipLabel}</span>
        <span className="filter-chip__caret">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50,
            minWidth: 240, maxHeight: 380, overflowY: 'auto',
            background: 'var(--wi-background-secondary)',
            border: '1px solid var(--wi-divider)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: 6,
          }}
        >
          {/* Header: filter label + Clear button */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 6px 6px',
          }}>
            <span className="eyebrow text-secondary" style={{ letterSpacing: '0.04em' }}>
              {filter.label}
            </span>
            {hasSelection && (
              <button type="button" onClick={clearAll} className="filter-clear">
                Clear
              </button>
            )}
          </div>

          {visibleGroups.length > 0 && (
            <div role="group" aria-labelledby={`filter-${filter.key}-groups-title`}>
              <SectionTitle id={`filter-${filter.key}-groups-title`}>Journey step</SectionTitle>
              {visibleGroups.map((g) => (
                <OptionRow
                  key={g.value}
                  opt={g}
                  checked={selected.includes(g.value)}
                  type={inputType}
                  name={`filter-${filter.key}-group`}
                  onToggle={() => toggleOption(g.value)}
                />
              ))}
            </div>
          )}

          <div role={visibleGroups.length > 0 ? 'group' : undefined} aria-labelledby={visibleGroups.length > 0 ? `filter-${filter.key}-options-title` : undefined}>
            {visibleGroups.length > 0 && <SectionTitle id={`filter-${filter.key}-options-title`}>Task type</SectionTitle>}
            {orderedOptions.length === 0 ? (
              <div style={{ padding: '6px 12px 10px' }}>
                <span className="detail1 text-secondary">No options in the current cohort.</span>
              </div>
            ) : (
              orderedOptions.map((opt) => (
                <OptionRow
                  key={opt.value}
                  opt={opt}
                  checked={selected.includes(opt.value)}
                  type={inputType}
                  name={`filter-${filter.key}`}
                  onToggle={() => toggleOption(opt.value)}
                />
              ))
            )}
          </div>

          {isAnd && count > 1 && (
            <div
              className="detail1 text-secondary"
              style={{ padding: '8px 10px 4px', marginTop: 4, borderTop: '1px solid var(--wi-divider)' }}
            >
              Patients must match all checked items.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ id, children }) {
  return (
    <div id={id} className="eyebrow text-secondary" style={{ padding: '8px 10px 2px', letterSpacing: '0.04em' }}>
      {children}
    </div>
  );
}

function OptionRow({ opt, checked, type, name, onToggle }) {
  const hasCount = typeof opt.count === 'number';
  return (
    <label className={`filter-option${checked ? ' is-checked' : ''}`}>
      <input
        type={type}
        name={name}
        checked={checked}
        onChange={onToggle}
        style={{ accentColor: 'var(--wi-action-primary)', cursor: 'pointer' }}
      />
      <span style={{ color: 'var(--wi-text-primary)', flex: 1 }}>{opt.label}</span>
      {hasCount && (
        <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {opt.count}
        </span>
      )}
    </label>
  );
}
